/**
 * ChartMind Prompt Editor
 * 
 * Editor component for ChartMind admin users to customize Position 3 prompts.
 * Features:
 * - Edit prompt text with syntax highlighting
 * - Configure model parameters
 * - Select input mode (A, B, C)
 * - Enable/disable individual prompts
 * - View Position 1 and 2 prompts (read-only)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Slider,
  Chip,
  Alert,
  Collapse,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestoreIcon from '@mui/icons-material/Restore';
import { INPUT_MODES } from '../../../../../services/llm/chainExecutionService';

// Input mode descriptions
const INPUT_MODE_INFO = {
  [INPUT_MODES.PREVIOUS_ONLY]: {
    label: 'Previous Output Only (A)',
    description: 'Your prompt receives only the output from the previous position in the chain.',
    example: 'Good for pure refinement tasks.',
  },
  [INPUT_MODES.PREVIOUS_PLUS_ORIGINAL]: {
    label: 'Previous + Original Input (B)',
    description: 'Your prompt receives both the previous output and the original user input.',
    example: 'Good for context-aware refinement.',
  },
  [INPUT_MODES.FULL_CONTEXT]: {
    label: 'Full Chain Context (C)',
    description: 'Your prompt receives the original input plus all previous outputs in the chain.',
    example: 'Good for comprehensive analysis with full history.',
  },
};

// Available models
const AVAILABLE_MODELS = [
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (Recommended)' },
  { value: 'gpt-4', label: 'GPT-4' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Faster)' },
];

/**
 * Single card prompt editor
 */
const CardPromptEditor = ({
  cardId,
  cardLabel,
  promptConfig,
  basePrompt,
  onSave,
  onToggle,
  isSaving,
  readOnly = false,
}) => {
  const [localPrompt, setLocalPrompt] = useState(promptConfig?.prompt || '');
  const [localModel, setLocalModel] = useState(promptConfig?.model || 'gpt-4-turbo');
  const [localTemperature, setLocalTemperature] = useState(promptConfig?.temperature || 0.7);
  const [localMaxTokens, setLocalMaxTokens] = useState(promptConfig?.maxTokens || 2000);
  const [localInputMode, setLocalInputMode] = useState(promptConfig?.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL);
  const [showBasePrompt, setShowBasePrompt] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    const changed = 
      localPrompt !== (promptConfig?.prompt || '') ||
      localModel !== (promptConfig?.model || 'gpt-4-turbo') ||
      localTemperature !== (promptConfig?.temperature || 0.7) ||
      localMaxTokens !== (promptConfig?.maxTokens || 2000) ||
      localInputMode !== (promptConfig?.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL);
    
    setHasChanges(changed);
  }, [localPrompt, localModel, localTemperature, localMaxTokens, localInputMode, promptConfig]);

  const handleSave = () => {
    onSave({
      prompt: localPrompt,
      model: localModel,
      temperature: localTemperature,
      maxTokens: localMaxTokens,
      inputMode: localInputMode,
    });
  };

  const handleReset = () => {
    setLocalPrompt(promptConfig?.prompt || '');
    setLocalModel(promptConfig?.model || 'gpt-4-turbo');
    setLocalTemperature(promptConfig?.temperature || 0.7);
    setLocalMaxTokens(promptConfig?.maxTokens || 2000);
    setLocalInputMode(promptConfig?.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL);
  };

  const isEnabled = promptConfig?.enabled || false;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{cardLabel}</Typography>
            <Chip 
              size="small" 
              label={isEnabled ? 'Active' : 'Inactive'} 
              color={isEnabled ? 'success' : 'default'}
            />
            {hasChanges && (
              <Chip size="small" label="Unsaved changes" color="warning" />
            )}
          </Box>
        }
        action={
          !readOnly && (
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => onToggle(e.target.checked)}
                  color="primary"
                />
              }
              label={isEnabled ? 'Enabled' : 'Disabled'}
            />
          )
        }
      />
      <Divider />
      <CardContent>
        {/* Base prompt view toggle */}
        {basePrompt && (
          <Accordion 
            expanded={showBasePrompt}
            onChange={(e, expanded) => setShowBasePrompt(expanded)}
            sx={{ mb: 2, backgroundColor: 'grey.50' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon fontSize="small" />
                <Typography>View Base Prompt (Position 1 & 2)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                This is the combined output from Position 1 (Global) and Position 2 (Regional) prompts.
                Your custom prompt will modify this output.
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={basePrompt}
                disabled
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Custom prompt editor */}
        <Typography variant="subtitle2" gutterBottom>
          Your Custom Prompt (Position 3)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={10}
          value={localPrompt}
          onChange={(e) => setLocalPrompt(e.target.value)}
          disabled={readOnly || !isEnabled}
          placeholder={isEnabled 
            ? "Enter your custom prompt here. This will be used to refine the output from the previous positions."
            : "Enable this prompt to edit."
          }
          sx={{
            mb: 2,
            '& .MuiInputBase-root': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
        />

        {/* Configuration options */}
        <Collapse in={isEnabled && !readOnly}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Input Mode */}
            <FormControl fullWidth>
              <InputLabel>Input Mode</InputLabel>
              <Select
                value={localInputMode}
                onChange={(e) => setLocalInputMode(e.target.value)}
                label="Input Mode"
              >
                {Object.entries(INPUT_MODE_INFO).map(([mode, info]) => (
                  <MenuItem key={mode} value={mode}>
                    <Box>
                      <Typography variant="body2">{info.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {info.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {INPUT_MODE_INFO[localInputMode]?.example}
              </Typography>
            </FormControl>

            {/* Model Selection */}
            <FormControl fullWidth>
              <InputLabel>Model</InputLabel>
              <Select
                value={localModel}
                onChange={(e) => setLocalModel(e.target.value)}
                label="Model"
              >
                {AVAILABLE_MODELS.map((model) => (
                  <MenuItem key={model.value} value={model.value}>
                    {model.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Temperature Slider */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Temperature: {localTemperature}
              </Typography>
              <Slider
                value={localTemperature}
                onChange={(e, value) => setLocalTemperature(value)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: 'Precise' },
                  { value: 0.5, label: 'Balanced' },
                  { value: 1, label: 'Creative' },
                ]}
              />
            </Box>

            {/* Max Tokens */}
            <Box>
              <Typography variant="body2" gutterBottom>
                Max Tokens: {localMaxTokens}
              </Typography>
              <Slider
                value={localMaxTokens}
                onChange={(e, value) => setLocalMaxTokens(value)}
                min={500}
                max={4000}
                step={100}
                marks={[
                  { value: 500, label: '500' },
                  { value: 2000, label: '2000' },
                  { value: 4000, label: '4000' },
                ]}
              />
            </Box>
          </Box>
        </Collapse>

        {/* Actions */}
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
            {hasChanges && (
              <Button
                startIcon={<RestoreIcon />}
                onClick={handleReset}
              >
                Reset
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || isSaving || !isEnabled}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Main Prompt Editor component
 */
const PromptEditor = ({
  adminId,
  customPrompts = {},
  basePrompts = {},
  onSavePrompt,
  onTogglePrompt,
  isSaving = false,
  readOnly = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const cardTabs = [
    { id: 'ddx', label: 'Differential Diagnosis' },
    { id: 'tests', label: 'Diagnostic Tests' },
    { id: 'treatment', label: 'Treatment Options' },
    { id: 'discharge', label: 'Discharge Plan' },
    { id: 'questions', label: 'Clarifying Questions' },
    { id: 'chartGeneration', label: 'Chart Generation' },
  ];

  const currentCard = cardTabs[activeTab];

  return (
    <Box>
      {/* Info banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Position 3 Customization:</strong> Your custom prompts modify the output 
          from Position 1 (Global) and Position 2 (Regional) prompts. These customizations 
          only affect your designees' experience.
        </Typography>
      </Alert>

      {/* Card tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {cardTabs.map((tab, index) => (
          <Tab
            key={tab.id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {tab.label}
                {customPrompts[tab.id]?.enabled && (
                  <Chip size="small" label="●" color="success" sx={{ minWidth: 'auto' }} />
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {/* Active card editor */}
      <CardPromptEditor
        cardId={currentCard.id}
        cardLabel={currentCard.label}
        promptConfig={customPrompts[currentCard.id]}
        basePrompt={basePrompts[currentCard.id]}
        onSave={(config) => onSavePrompt(currentCard.id, config)}
        onToggle={(enabled) => onTogglePrompt(currentCard.id, enabled)}
        isSaving={isSaving}
        readOnly={readOnly}
      />
    </Box>
  );
};

export default PromptEditor;
